import type { JSX } from 'react';
import type {
  ColumnDef,
  DateRangeFilterValue,
  FilterValue,
} from '@/shared/types/table-filters.types';
import { ANY_FILTER_VALUE, EMPTY_FILTER_VALUE } from '@/shared/utils/tableFilters';
import { DateRangeFilter } from './DateRangeFilter';
import { FilterSelect, type FilterSelectOption } from './FilterSelect';
import { MultiSelectFilter } from './MultiSelectFilter';
import { TextFilter } from './TextFilter';

export interface TableFilterControlProps<T> {
  column: ColumnDef<T>;
  value: FilterValue | undefined;
  scope: string;
  onChange: (value: FilterValue | undefined) => void;
}

const BOOLEAN_OPTIONS: FilterSelectOption[] = [
  { value: ANY_FILTER_VALUE, label: 'Any' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
  { value: EMPTY_FILTER_VALUE, label: 'Empty' },
];

function selectOptions(
  options: string[] = [],
  labels: Record<string, string> = {}
): FilterSelectOption[] {
  return [
    { value: ANY_FILTER_VALUE, label: 'All' },
    ...options.map((option) => ({ value: option, label: labels[option] ?? option })),
    { value: EMPTY_FILTER_VALUE, label: 'Empty' },
  ];
}

function asText(value: FilterValue | undefined): string {
  return typeof value === 'string' ? value : '';
}

function asList(value: FilterValue | undefined): string[] {
  return Array.isArray(value) ? value : [];
}

function asRange(value: FilterValue | undefined): DateRangeFilterValue {
  return typeof value === 'object' && !Array.isArray(value) ? value : {};
}

/** Renders the control matching a column's `filterType`. */
export function TableFilterControl<T>({
  column,
  value,
  scope,
  onChange,
}: TableFilterControlProps<T>): JSX.Element {
  const id = `${scope}-filter-${column.key}`;
  const { filterType, label, options, optionLabels } = column;

  if (filterType === 'date-range') {
    return <DateRangeFilter id={id} label={label} value={asRange(value)} onChange={onChange} />;
  }
  if (filterType === 'multi-select') {
    return (
      <MultiSelectFilter
        id={id}
        label={label}
        value={asList(value)}
        options={options ?? []}
        onChange={onChange}
      />
    );
  }
  if (filterType === 'text') {
    return <TextFilter id={id} label={label} value={asText(value)} onChange={onChange} />;
  }

  const isBoolean = filterType === 'boolean';
  return (
    <FilterSelect
      id={id}
      label={label}
      value={asText(value) || undefined}
      options={isBoolean ? BOOLEAN_OPTIONS : selectOptions(options, optionLabels)}
      onChange={onChange}
    />
  );
}
